import type { LegendOverrides } from '../legend-editor'
import { useMapStateStore } from './core'

/**
 * Legend overrides are used to modify the appearance of the legend,
 * and these are the required properties to derive the legend from the gradient.
 *
 * Note that ranges and bin counts are unique to continuous legends,
 * while blue is unique to discrete legends.
 */
export type Override = LegendOverrides

/**
 * Key for an as-applied legend override entry. Scoped per-primary-stream so that
 * switching streams (different field, season, etc.) starts with a fresh
 * legend — the user's data context has changed and a customization
 * calibrated to the prior stream's value distribution may not apply.
 *
 * Deliberately does NOT include `mergedStreamIds`: turning stream-merging
 * on or off keeps the primary stream the same and should preserve the
 * user's legend edits. The override follows the primary, not the merge set.
 */
type AsAppliedLegendKey = {
  streamId: string
  applicationId: string
  layerId: string
}

/**
 * Key for a soil sample legend override entry. Scoped to the analyte and not
 * only the dataset, because the bounds a user sets are bounds on one analyte's
 * values: phosphorus in ppm and pH share no scale.
 */
type SoilSampleLegendKey = {
  planningFieldId: string
  samplingEventId: string
  measurement: string
}

/**
 * What a legend override attaches to. A union rather than one widened shape so
 * that a layer kind with no legend of its own cannot accidentally address one.
 */
type LegendKey = AsAppliedLegendKey | SoilSampleLegendKey

const toKey = (key: LegendKey) =>
  'streamId' in key
    ? `${key.streamId}/${key.applicationId}/${key.layerId}`
    : `soil/${key.planningFieldId}/${key.samplingEventId}/${key.measurement}`

const setHighlightOptions = (
  layer: LegendKey,
  options: Pick<Override, 'isHighlightMode' | 'highlightLabel'>
) => {
  const key = toKey(layer)

  useMapStateStore.setState((prev) => {
    const next = new Map(prev.legendOverrides)
    const old = next.get(key)
    if (old) {
      next.set(key, { ...old, ...options })
    } else {
      next.set(key, options)
    }
    return { legendOverrides: next }
  })
}

const setActiveRange = (layer: LegendKey, activeRange: { low: number; high: number }) => {
  const key = toKey(layer)

  useMapStateStore.setState((prev) => {
    const next = new Map(prev.legendOverrides)
    const old = next.get(key)
    if (old) {
      next.set(key, { ...old, activeRange })
    } else {
      next.set(key, { activeRange })
    }
    return { legendOverrides: next }
  })
}

const setContinuousBinCount = (layer: LegendKey, continuousBinCount: number) => {
  const key = toKey(layer)

  useMapStateStore.setState((prev) => {
    const next = new Map(prev.legendOverrides)
    const old = next.get(key)
    if (old) {
      next.set(key, { ...old, continuousBinCount })
    } else {
      next.set(key, { continuousBinCount })
    }
    return { legendOverrides: next }
  })
}

const setEditableRange = (layer: LegendKey, editableRange: { low: number; high: number }) => {
  const key = toKey(layer)

  useMapStateStore.setState((prev) => {
    const next = new Map(prev.legendOverrides)
    const old = next.get(key)
    if (old) {
      next.set(key, { ...old, editableRange })
    } else {
      next.set(key, { editableRange })
    }
    return { legendOverrides: next }
  })
}

const toggleHighlight = (layer: LegendKey) => {
  const key = toKey(layer)

  useMapStateStore.setState((prev) => {
    const old = prev.legendOverrides.get(key)
    const next = new Map(prev.legendOverrides)
    const isHighlightMode = old?.isHighlightMode !== true
    return {
      legendOverrides: next.set(key, { ...old, isHighlightMode })
    }
  })
}

const restore = (layer: LegendKey) => {
  const key = toKey(layer)

  useMapStateStore.setState((prev) => {
    const next = new Map(prev.legendOverrides)
    next.delete(key)
    return { legendOverrides: next }
  })
}

export const useLegendOverrides = (layer: LegendKey) => {
  const overrides = useMapStateStore((state) => state.legendOverrides)
  return overrides.get(toKey(layer))
}

export const updateLegend = {
  setHighlightOptions,
  setActiveRange,
  setContinuousBinCount,
  setEditableRange,
  toggleHighlight,
  restore
}
